/**
 * Modal to extend an existing license's expiry date.
 *
 * Extracted from LicenseManagementPage.tsx (Sub-PR 6.3).
 */

import { useState } from 'react'
import type { License } from '../../types'
import { type ExtendFormData, formatDate, toDateInputValue } from './helpers'

interface ExtendModalProps {
  license: License
  onClose: () => void
  onExtend: (validUntil: string) => Promise<void>
}

function ModalHeader({ onClose }: { onClose: () => void }): React.ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 dark:border-stone-700">
      <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">Extend License</h2>
      <button
        onClick={onClose}
        className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
}

function CurrentLicenseSummary({ license }: { license: License }): React.ReactElement {
  const expiryDisplay = license.validUntil ? formatDate(license.validUntil) : 'Never'
  return (
    <div className="rounded-lg bg-stone-50 p-3 dark:bg-stone-700/50">
      <p className="text-sm text-stone-600 dark:text-stone-400">
        <span className="font-medium text-stone-900 dark:text-stone-100">License Key:</span>{' '}
        <code className="rounded bg-stone-200 px-1.5 py-0.5 text-xs dark:bg-stone-600">
          {license.licenseKey}
        </code>
      </p>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        <span className="font-medium text-stone-900 dark:text-stone-100">Current Expiry:</span>{' '}
        {expiryDisplay}
      </p>
    </div>
  )
}

interface DateFieldProps {
  validUntil: string
  setValidUntil: (value: string) => void
}

function DateField({ validUntil, setValidUntil }: DateFieldProps): React.ReactElement {
  const todayIso = new Date().toISOString().split('T')[0] ?? ''
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
        New Expiry Date *
      </label>
      <input
        type="date"
        value={validUntil}
        onChange={(event) => {
          setValidUntil(event.target.value)
        }}
        min={todayIso}
        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
        required
      />
    </div>
  )
}

function FormActions({
  saving,
  onClose,
}: {
  saving: boolean
  onClose: () => void
}): React.ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={saving}
        className="rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {saving ? 'Extending...' : 'Extend License'}
      </button>
    </div>
  )
}

export function ExtendModal({ license, onClose, onExtend }: ExtendModalProps): React.ReactElement {
  const [formData, setFormData] = useState<ExtendFormData>({
    validUntil: toDateInputValue(license.validUntil),
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (!formData.validUntil) {
      setError('Please select a new expiry date')
      return
    }
    setError(null)
    setSaving(true)
    try {
      await onExtend(formData.validUntil)
      onClose()
    } catch (extendError) {
      setError(extendError instanceof Error ? extendError.message : 'Failed to extend license')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <ModalHeader onClose={onClose} />
        <form
          onSubmit={(event) => {
            void handleSubmit(event)
          }}
          className="space-y-4 p-6"
        >
          {error !== null && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}
          <CurrentLicenseSummary license={license} />
          <DateField
            validUntil={formData.validUntil}
            setValidUntil={(validUntil) => {
              setFormData({ validUntil })
            }}
          />
          <FormActions saving={saving} onClose={onClose} />
        </form>
      </div>
    </div>
  )
}
